import { useState } from 'react';
import { Link } from 'react-router';
import { AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import { type Banner as BannerType } from '@/hooks/useBanners';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface BannerProps {
  banner: BannerType;
}

const DISMISSED_BANNERS_KEY = 'dismissedBanners';
const DISMISSAL_TTL_DAYS = 30;

interface DismissedBannersStore {
  [bannerId: string]: number;
}

/**
 * Banner — single sticky notification rendered at the top of AppShell.
 * Supports info / warning / error severities, optional CTAs, and
 * per-banner dismissal with 30-day TTL persisted to localStorage.
 *
 * Visual treatment uses the spec §10.3 severity tokens: sev-5 (info),
 * sev-3 (warning), sev-1 (error). Same palette as Incidents so the
 * meaning is consistent across the product.
 */
export default function Banner({ banner }: BannerProps) {
  const [isDismissed, setIsDismissed] = useState(false);

  // Checked during render rather than in an effect, so a banner the user already
  // dismissed never flashes up for a frame first.
  const dismissKey = `${banner.id}:${String(banner.dismissible)}`;
  const [checkedKey, setCheckedKey] = useState<string | null>(null);
  if (dismissKey !== checkedKey) {
    setCheckedKey(dismissKey);
    if (banner.dismissible && isBannerDismissed(banner.id)) setIsDismissed(true);
  }

  if (isDismissed) return null;

  const handleDismiss = () => {
    dismissBanner(banner.id);
    setIsDismissed(true);
  };

  const Icon =
    banner.severity === 'error'
      ? AlertCircle
      : banner.severity === 'warning'
        ? AlertTriangle
        : Info;

  const severityClass = {
    info: 'border-sev-5 bg-sev-5-soft text-foreground',
    warning: 'border-sev-3 bg-sev-3-soft text-foreground',
    error: 'border-sev-1 bg-sev-1-soft text-foreground',
  }[banner.severity];

  const iconClass = {
    info: 'text-sev-5',
    warning: 'text-sev-3',
    error: 'text-sev-1',
  }[banner.severity];

  return (
    <div className={cn('border-b px-4 md:px-6 py-3 relative', severityClass)}>
      <div className="max-w-[1600px] mx-auto flex items-start gap-3">
        <Icon className={cn('w-5 h-5 flex-shrink-0 mt-0.5', iconClass)} aria-hidden="true" />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">{banner.title}</p>
          <p className="text-sm mt-0.5">{banner.message}</p>
          {banner.cta ? (
            <div className="mt-2.5">
              {banner.cta.type === 'internal' ? (
                <Button asChild size="sm" variant="outline">
                  <Link to={banner.cta.href}>{banner.cta.text}</Link>
                </Button>
              ) : (
                <Button asChild size="sm" variant="outline">
                  <a
                    href={banner.cta.href}
                    {...(banner.cta.type === 'external'
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                  >
                    {banner.cta.text}
                  </a>
                </Button>
              )}
            </div>
          ) : null}
        </div>
        {banner.dismissible ? (
          <button
            type="button"
            onClick={handleDismiss}
            className="inline-flex h-8 w-8 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground flex-shrink-0"
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        ) : null}
      </div>
    </div>
  );
}

// ─── localStorage helpers (unchanged from Bulma original) ─────────────

function getDismissedBannersStore(): DismissedBannersStore {
  try {
    const stored = localStorage.getItem(DISMISSED_BANNERS_KEY);
    if (!stored) return {};
    const store = JSON.parse(stored) as DismissedBannersStore;
    const now = Date.now();
    const ttlMs = DISMISSAL_TTL_DAYS * 24 * 60 * 60 * 1000;
    const cleaned: DismissedBannersStore = {};
    let hasExpired = false;
    for (const [bannerId, timestamp] of Object.entries(store)) {
      if (now - timestamp < ttlMs) {
        cleaned[bannerId] = timestamp;
      } else {
        hasExpired = true;
      }
    }
    if (hasExpired) {
      localStorage.setItem(DISMISSED_BANNERS_KEY, JSON.stringify(cleaned));
    }
    return cleaned;
  } catch (error) {
    console.error('Failed to parse dismissed banners from localStorage:', error);
    return {};
  }
}

function isBannerDismissed(bannerId: string): boolean {
  return bannerId in getDismissedBannersStore();
}

function dismissBanner(bannerId: string): void {
  try {
    const store = getDismissedBannersStore();
    store[bannerId] = Date.now();
    localStorage.setItem(DISMISSED_BANNERS_KEY, JSON.stringify(store));
  } catch (error) {
    console.error('Failed to save dismissed banner to localStorage:', error);
  }
}
